import { useState, useEffect, useRef } from "react";

/* ══════════════════════════════════════════
   HOOKS
══════════════════════════════════════════ */
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setInView(true); },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

function FadeIn({ children, delay = 0, className = "" }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} className={className} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0)" : "translateY(22px)",
      transition: `opacity 0.6s ease ${delay}s, transform 0.6s ease ${delay}s`,
    }}>{children}</div>
  );
}

/* ══════════════════════════════════════════
   SVG ICON SYSTEM
══════════════════════════════════════════ */
function Ico({ s = 20, c = "currentColor", sw = 1.75, fill = "none", children }) {
  return (
    <svg width={s} height={s} viewBox="0 0 24 24" fill={fill}
      stroke={c} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0, display: "block" }}>
      {children}
    </svg>
  );
}

const I = {
  Code:     (p) => <Ico {...p}><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></Ico>,
  Layers:   (p) => <Ico {...p}><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></Ico>,
  Globe:    (p) => <Ico {...p}><circle cx="12" cy="12" r="10"/><line x1="2" y1="12" x2="22" y2="12"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></Ico>,
  Smartphone:(p) => <Ico {...p}><rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></Ico>,
  Tool:     (p) => <Ico {...p}><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/></Ico>,
  Cpu:      (p) => <Ico {...p}><rect x="9" y="9" width="6" height="6"/><rect x="2" y="2" width="20" height="20" rx="2"/><line x1="9" y1="2" x2="9" y2="6"/><line x1="15" y1="2" x2="15" y2="6"/><line x1="9" y1="18" x2="9" y2="22"/><line x1="15" y1="18" x2="15" y2="22"/><line x1="2" y1="9" x2="6" y2="9"/><line x1="2" y1="15" x2="6" y2="15"/><line x1="18" y1="9" x2="22" y2="9"/><line x1="18" y1="15" x2="22" y2="15"/></Ico>,
  Server:   (p) => <Ico {...p}><rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/><line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/></Ico>,
  Mail:     (p) => <Ico {...p}><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></Ico>,
  MapPin:   (p) => <Ico {...p}><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></Ico>,
  Clock:    (p) => <Ico {...p}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></Ico>,
  Moon:     (p) => <Ico {...p}><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></Ico>,
  Dollar:   (p) => <Ico {...p}><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></Ico>,
  Shield:   (p) => <Ico {...p}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></Ico>,
  Lock:     (p) => <Ico {...p}><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></Ico>,
  Check:    (p) => <Ico {...p}><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></Ico>,
  Arrow:    (p) => <Ico {...p}><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></Ico>,
  ChevL:    (p) => <Ico {...p}><polyline points="15 18 9 12 15 6"/></Ico>,
  Monitor:  (p) => <Ico {...p}><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></Ico>,
  Bar:      (p) => <Ico {...p}><line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/></Ico>,
  Heart:    (p) => <Ico {...p}><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></Ico>,
  Activity: (p) => <Ico {...p}><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></Ico>,
  Cal:      (p) => <Ico {...p}><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></Ico>,
  Menu:     (p) => <Ico {...p}><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="18" x2="21" y2="18"/></Ico>,
  X:        (p) => <Ico {...p}><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></Ico>,
  Users:    (p) => <Ico {...p}><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></Ico>,
  Zap:      (p) => <Ico {...p}><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></Ico>,
  Star:     (p) => <Ico {...p}><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></Ico>,
};

/* ══════════════════════════════════════════
   LUNACYCLE PHONE MOCKUP
══════════════════════════════════════════ */
function LunaPhone() {
  const weeks = [
    [1,2,3,4,5,6,7],[8,9,10,11,12,13,14],
    [15,16,17,18,19,20,21],[22,23,24,25,26,27,28],
    [29,30,null,null,null,null,null],
  ];
  const ds = {
    period:  { background:"rgba(244,63,138,0.7)", color:"white", borderRadius:4 },
    fertile: { background:"rgba(52,211,153,0.18)", color:"#6ee7b7", borderRadius:4 },
    ovu:     { background:"#34d399", color:"#022c22", borderRadius:4, fontWeight:700 },
    today:   { outline:"1.5px solid #c084fc", color:"#e9d5ff", borderRadius:4, fontWeight:700 },
    pred:    { background:"rgba(244,63,138,0.18)", color:"rgba(244,63,138,0.6)", borderRadius:4 },
    normal:  { color:"rgba(255,255,255,0.45)" },
  };
  const meta = (d) => {
    if (!d) return null;
    if (d <= 5) return "period";
    if (d === 15) return "ovu";
    if (d >= 12 && d <= 18) return "fertile";
    if (d === 26) return "today";
    if (d >= 29) return "pred";
    return "normal";
  };
  return (
    <div style={{width:200,height:420,background:"linear-gradient(160deg,#1a0825,#2d1060)",borderRadius:32,border:"2px solid rgba(255,255,255,0.1)",boxShadow:"0 32px 64px rgba(0,0,0,0.55),inset 0 1px 0 rgba(255,255,255,0.08)",display:"flex",flexDirection:"column",fontFamily:"'Inter',sans-serif",overflow:"hidden"}}>
      <div style={{height:26,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <div style={{width:70,height:16,background:"#0a0010",borderRadius:"0 0 10px 10px"}}/>
      </div>
      <div style={{flex:1,padding:"4px 13px 0",overflow:"hidden"}}>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:9}}>
          <div style={{width:22,height:22,borderRadius:"50%",background:"linear-gradient(135deg,#a855f7,#ec4899)",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <I.Moon s={11} c="white"/>
          </div>
          <span style={{color:"white",fontWeight:700,fontSize:13}}>LunaCycle</span>
        </div>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:6}}>
          <span style={{color:"rgba(255,255,255,0.3)",fontSize:10}}>◂</span>
          <span style={{color:"white",fontWeight:600,fontSize:11}}>June 2026</span>
          <span style={{color:"rgba(255,255,255,0.3)",fontSize:10}}>▸</span>
        </div>
        <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",marginBottom:2}}>
          {["M","T","W","T","F","S","S"].map((d,i)=>(
            <div key={i} style={{textAlign:"center",color:"rgba(255,255,255,0.28)",fontSize:8,fontWeight:600,paddingBottom:2}}>{d}</div>
          ))}
        </div>
        {weeks.map((wk,wi)=>(
          <div key={wi} style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",marginBottom:1}}>
            {wk.map((d,di)=>{
              const m = meta(d);
              return <div key={di} style={{textAlign:"center",fontSize:9,padding:"3px 1px",...(m?ds[m]:{})}}>
                {d||""}
              </div>;
            })}
          </div>
        ))}
        <div style={{marginTop:7,padding:"6px 8px",background:"rgba(168,85,247,0.18)",borderRadius:7,border:"1px solid rgba(168,85,247,0.28)"}}>
          <div style={{color:"#e9d5ff",fontSize:10,fontWeight:600}}>Day 26 · Late Luteal</div>
          <div style={{color:"rgba(233,213,255,0.5)",fontSize:8,marginTop:1}}>Next period in ~3 days</div>
        </div>
        <div style={{marginTop:6,display:"flex",gap:4}}>
          {[["💧","Flow"],["😊","Mood"],["📝","Log"]].map(([ic,lb])=>(
            <div key={lb} style={{flex:1,background:"rgba(255,255,255,0.05)",borderRadius:6,padding:"5px 2px",textAlign:"center"}}>
              <div style={{fontSize:10}}>{ic}</div>
              <div style={{color:"rgba(255,255,255,0.35)",fontSize:7,marginTop:1}}>{lb}</div>
            </div>
          ))}
        </div>
      </div>
      <div style={{height:24,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <div style={{width:50,height:3,background:"rgba(255,255,255,0.18)",borderRadius:2}}/>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   LUNACYCLE DESKTOP MOCKUP
══════════════════════════════════════════ */
function LunaDesktop() {
  return (
    <div style={{position:"relative",width:330}}>
      <div style={{background:"#0e041d",border:"2.5px solid rgba(168,85,247,0.18)",borderRadius:"10px 10px 0 0",overflow:"hidden",boxShadow:"0 24px 48px rgba(0,0,0,0.5)"}}>
        <div style={{background:"#08011a",padding:"7px 10px",display:"flex",alignItems:"center",gap:5}}>
          <div style={{display:"flex",gap:4}}>
            {["#ff5f57","#febc2e","#28c840"].map(c=><div key={c} style={{width:7,height:7,borderRadius:"50%",background:c}}/>)}
          </div>
          <div style={{flex:1,background:"rgba(255,255,255,0.05)",borderRadius:4,padding:"2px 7px",marginLeft:5,display:"flex",alignItems:"center",gap:4}}>
            <I.Lock s={7} c="rgba(255,255,255,0.22)"/>
            <span style={{color:"rgba(255,255,255,0.3)",fontSize:8}}>lunacycle.app</span>
          </div>
        </div>
        <div style={{display:"flex",height:186,fontFamily:"'Inter',sans-serif"}}>
          <div style={{width:42,background:"#06000f",display:"flex",flexDirection:"column",alignItems:"center",paddingTop:10,gap:10}}>
            <div style={{width:22,height:22,borderRadius:6,background:"linear-gradient(135deg,#a855f7,#ec4899)",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:4}}>
              <I.Moon s={10} c="white"/>
            </div>
            {[I.Cal,I.Bar,I.Heart,I.Activity].map((Ic,i)=>(
              <div key={i} style={{width:24,height:24,borderRadius:5,background:i===0?"rgba(168,85,247,0.18)":"transparent",display:"flex",alignItems:"center",justifyContent:"center"}}>
                <Ic s={12} c={i===0?"#a855f7":"rgba(255,255,255,0.18)"}/>
              </div>
            ))}
          </div>
          <div style={{flex:1,padding:"10px 11px",overflow:"hidden"}}>
            <div style={{color:"rgba(255,255,255,0.35)",fontSize:7,textTransform:"uppercase",letterSpacing:0.8,marginBottom:7}}>June 2026 Overview</div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:5,marginBottom:7}}>
              {[["Cycle Day","26","Late Luteal"],["Next Period","~3d","June 29"],["Avg Cycle","29d","6 months"],["Log Streak","14d","Keep going!"]].map(([l,v,s])=>(
                <div key={l} style={{background:"rgba(168,85,247,0.1)",border:"1px solid rgba(168,85,247,0.14)",borderRadius:6,padding:"5px 6px"}}>
                  <div style={{color:"rgba(255,255,255,0.3)",fontSize:7}}>{l}</div>
                  <div style={{color:"white",fontWeight:800,fontSize:13,lineHeight:1.1}}>{v}</div>
                  <div style={{color:"#a855f7",fontSize:7}}>{s}</div>
                </div>
              ))}
            </div>
            <div style={{background:"rgba(255,255,255,0.03)",borderRadius:6,padding:"6px 7px"}}>
              <div style={{color:"rgba(255,255,255,0.3)",fontSize:7,marginBottom:4}}>Cycle History (days)</div>
              <div style={{display:"flex",gap:3,alignItems:"flex-end",height:22}}>
                {[28,30,29,27,29,30,29].map((v,i)=>(
                  <div key={i} style={{flex:1,background:i===6?"#a855f7":"rgba(168,85,247,0.22)",borderRadius:2,height:`${((v-26)/(33-26))*100}%`,minHeight:3}}/>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={{background:"linear-gradient(to bottom,#180636,#0a021a)",height:11,borderRadius:"0 0 4px 4px"}}>
        <div style={{margin:"0 auto",width:"33%",height:"100%",background:"#0e041d",borderRadius:"0 0 4px 4px"}}/>
      </div>
      <div style={{background:"linear-gradient(to bottom,rgba(24,6,54,0.6),transparent)",height:5,borderRadius:"0 0 50px 50px",width:"108%",marginLeft:"-4%"}}/>
    </div>
  );
}

/* ══════════════════════════════════════════
   SPLITEASE PHONE MOCKUP
══════════════════════════════════════════ */
function SplitPhone() {
  return (
    <div style={{width:200,height:420,background:"linear-gradient(160deg,#060d1c,#0a1830)",borderRadius:32,border:"2px solid rgba(255,255,255,0.09)",boxShadow:"0 32px 64px rgba(0,0,0,0.55),inset 0 1px 0 rgba(255,255,255,0.05)",display:"flex",flexDirection:"column",fontFamily:"'Inter',sans-serif",overflow:"hidden"}}>
      <div style={{height:26,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <div style={{width:70,height:16,background:"#030810",borderRadius:"0 0 10px 10px"}}/>
      </div>
      <div style={{flex:1,padding:"6px 13px 0",overflow:"hidden"}}>
        <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:14}}>
          <div style={{width:22,height:22,borderRadius:7,background:"linear-gradient(135deg,#0d9488,#0891b2)",display:"flex",alignItems:"center",justifyContent:"center"}}>
            <I.Dollar s={11} c="white"/>
          </div>
          <span style={{color:"white",fontWeight:700,fontSize:13}}>SplitEase</span>
        </div>
        <div style={{textAlign:"center",marginBottom:12}}>
          <div style={{color:"rgba(255,255,255,0.3)",fontSize:8,textTransform:"uppercase",letterSpacing:0.5,marginBottom:1}}>Total Amount</div>
          <div style={{color:"white",fontWeight:800,fontSize:28,letterSpacing:-1}}>R 900</div>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:10}}>
          <div style={{flex:1,height:1,background:"rgba(255,255,255,0.06)"}}/>
          <span style={{color:"rgba(255,255,255,0.3)",fontSize:8}}>split between 3</span>
          <div style={{flex:1,height:1,background:"rgba(255,255,255,0.06)"}}/>
        </div>
        <div style={{display:"flex",flexDirection:"column",gap:5,marginBottom:12}}>
          {[{n:"Alice",s:"R 300",c:"#0d9488"},{n:"Bob",s:"R 300",c:"#0891b2"},{n:"Carl",s:"R 300",c:"#6366f1"}].map(p=>(
            <div key={p.n} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"rgba(255,255,255,0.04)",borderRadius:7,padding:"6px 9px"}}>
              <div style={{display:"flex",alignItems:"center",gap:6}}>
                <div style={{width:18,height:18,borderRadius:"50%",background:p.c,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:8,fontWeight:700}}>{p.n[0]}</div>
                <span style={{color:"rgba(255,255,255,0.6)",fontSize:10}}>{p.n}</span>
              </div>
              <span style={{color:"white",fontWeight:600,fontSize:11}}>{p.s}</span>
            </div>
          ))}
        </div>
        <div style={{background:"linear-gradient(135deg,#0d9488,#0891b2)",borderRadius:9,padding:9,textAlign:"center"}}>
          <span style={{color:"white",fontWeight:700,fontSize:12}}>Split Equally →</span>
        </div>
      </div>
      <div style={{height:24,display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
        <div style={{width:50,height:3,background:"rgba(255,255,255,0.16)",borderRadius:2}}/>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   SPLITEASE DESKTOP MOCKUP
══════════════════════════════════════════ */
function SplitDesktop() {
  return (
    <div style={{position:"relative",width:330}}>
      <div style={{background:"#030810",border:"2.5px solid rgba(13,148,136,0.18)",borderRadius:"10px 10px 0 0",overflow:"hidden",boxShadow:"0 24px 48px rgba(0,0,0,0.5)"}}>
        <div style={{background:"#050e1c",padding:"7px 10px",display:"flex",alignItems:"center",gap:5}}>
          <div style={{display:"flex",gap:4}}>
            {["#ff5f57","#febc2e","#28c840"].map(c=><div key={c} style={{width:7,height:7,borderRadius:"50%",background:c}}/>)}
          </div>
          <div style={{flex:1,background:"rgba(255,255,255,0.05)",borderRadius:4,padding:"2px 7px",marginLeft:5,display:"flex",alignItems:"center",gap:4}}>
            <I.Lock s={7} c="rgba(255,255,255,0.22)"/>
            <span style={{color:"rgba(255,255,255,0.3)",fontSize:8}}>splitease.app</span>
          </div>
        </div>
        <div style={{display:"flex",height:186,fontFamily:"'Inter',sans-serif"}}>
          <div style={{flex:1,padding:"10px 10px",borderRight:"1px solid rgba(255,255,255,0.05)"}}>
            <div style={{color:"rgba(255,255,255,0.35)",fontSize:7,textTransform:"uppercase",letterSpacing:0.8,marginBottom:7}}>Expenses</div>
            <div style={{display:"flex",flexDirection:"column",gap:4,marginBottom:7}}>
              {[{n:"Dinner at Salt",a:"R 450",t:"Food"},{n:"Uber rides",a:"R 150",t:"Travel"},{n:"Drinks round",a:"R 300",t:"Drinks"}].map(e=>(
                <div key={e.n} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"rgba(255,255,255,0.04)",borderRadius:5,padding:"5px 7px"}}>
                  <div>
                    <div style={{color:"rgba(255,255,255,0.75)",fontSize:8,fontWeight:500}}>{e.n}</div>
                    <div style={{color:"rgba(255,255,255,0.28)",fontSize:7}}>{e.t}</div>
                  </div>
                  <div style={{color:"#0d9488",fontSize:9,fontWeight:600}}>{e.a}</div>
                </div>
              ))}
            </div>
            <div style={{display:"flex",justifyContent:"space-between",padding:"5px 7px",borderTop:"1px solid rgba(255,255,255,0.04)"}}>
              <span style={{color:"rgba(255,255,255,0.35)",fontSize:8}}>Total</span>
              <span style={{color:"white",fontWeight:700,fontSize:10}}>R 900</span>
            </div>
            <div style={{marginTop:7,background:"linear-gradient(135deg,#0d9488,#0891b2)",borderRadius:5,padding:"5px 8px",textAlign:"center"}}>
              <span style={{color:"white",fontWeight:700,fontSize:9}}>Add Expense +</span>
            </div>
          </div>
          <div style={{width:126,padding:"10px 10px"}}>
            <div style={{color:"rgba(255,255,255,0.35)",fontSize:7,textTransform:"uppercase",letterSpacing:0.8,marginBottom:7}}>Per Person</div>
            {[{n:"Alice",a:"R 300",c:"#0d9488"},{n:"Bob",a:"R 300",c:"#0891b2"},{n:"Carl",a:"R 300",c:"#6366f1"}].map(p=>(
              <div key={p.n} style={{display:"flex",alignItems:"center",gap:6,marginBottom:7}}>
                <div style={{width:20,height:20,borderRadius:"50%",background:p.c,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontSize:8,fontWeight:700,flexShrink:0}}>{p.n[0]}</div>
                <div>
                  <div style={{color:"rgba(255,255,255,0.45)",fontSize:8}}>{p.n}</div>
                  <div style={{color:"white",fontWeight:700,fontSize:10}}>{p.a}</div>
                </div>
              </div>
            ))}
            <div style={{marginTop:6,background:"rgba(13,148,136,0.12)",border:"1px solid rgba(13,148,136,0.25)",borderRadius:5,padding:"5px 6px",textAlign:"center"}}>
              <div style={{color:"rgba(255,255,255,0.35)",fontSize:7}}>No account needed</div>
              <div style={{color:"#0d9488",fontSize:8,fontWeight:600,marginTop:1}}>100% Private</div>
            </div>
          </div>
        </div>
      </div>
      <div style={{background:"linear-gradient(to bottom,#0c1929,#050e1c)",height:11,borderRadius:"0 0 4px 4px"}}>
        <div style={{margin:"0 auto",width:"33%",height:"100%",background:"#030810",borderRadius:"0 0 4px 4px"}}/>
      </div>
      <div style={{background:"linear-gradient(to bottom,rgba(5,14,28,0.6),transparent)",height:5,borderRadius:"0 0 50px 50px",width:"108%",marginLeft:"-4%"}}/>
    </div>
  );
}

function TrotDesktop() {
  return (
    <div style={{position:"relative",width:330}}>
      <div style={{background:"#030810",border:"2.5px solid rgba(217,119,6,0.18)",borderRadius:"10px 10px 0 0",overflow:"hidden",boxShadow:"0 24px 48px rgba(0,0,0,0.5)"}}>
        <div style={{background:"#050e1c",padding:"7px 10px",display:"flex",alignItems:"center",gap:5}}>
          <div style={{display:"flex",gap:4}}>
            {["#ff5f57","#febc2e","#28c840"].map(c=><div key={c} style={{width:7,height:7,borderRadius:"50%",background:c}}/>)}
          </div>
          <div style={{flex:1,background:"rgba(255,255,255,0.05)",borderRadius:4,padding:"2px 7px",marginLeft:5,display:"flex",alignItems:"center",gap:4}}>
            <I.Lock s={7} c="rgba(255,255,255,0.22)"/>
            <span style={{color:"rgba(255,255,255,0.3)",fontSize:8}}>trotsoudtshoorn.co.za</span>
          </div>
        </div>
        <div style={{padding:"14px 12px",fontFamily:"'Inter',sans-serif",height:186,display:"flex",flexDirection:"column"}}>
          <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:10}}>
            <div style={{width:20,height:20,borderRadius:6,background:"linear-gradient(135deg,#d97706,#b45309)",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <I.MapPin s={11} c="white"/>
            </div>
            <span style={{color:"white",fontWeight:700,fontSize:10}}>Trots Oudtshoorn</span>
          </div>
          <div style={{display:"flex",flexDirection:"column",gap:6,marginBottom:10}}>
            {[{n:"Café De Kelder",t:"Restaurant"},{n:"CP Nel Museum",t:"Attraction"},{n:"Route 62 Guest House",t:"Accommodation"}].map(e=>(
              <div key={e.n} style={{display:"flex",justifyContent:"space-between",alignItems:"center",background:"rgba(255,255,255,0.04)",borderRadius:5,padding:"6px 8px"}}>
                <div style={{color:"rgba(255,255,255,0.75)",fontSize:8,fontWeight:500}}>{e.n}</div>
                <div style={{color:"rgba(217,119,6,0.85)",fontSize:7,fontWeight:600}}>{e.t}</div>
              </div>
            ))}
          </div>
          <div style={{marginTop:"auto",background:"linear-gradient(135deg,#d97706,#b45309)",borderRadius:5,padding:"6px 8px",textAlign:"center"}}>
            <span style={{color:"white",fontWeight:700,fontSize:9}}>Explore Oudtshoorn →</span>
          </div>
        </div>
      </div>
      <div style={{background:"linear-gradient(to bottom,#0c1929,#050e1c)",height:11,borderRadius:"0 0 4px 4px"}}>
        <div style={{margin:"0 auto",width:"33%",height:"100%",background:"#030810",borderRadius:"0 0 4px 4px"}}/>
      </div>
      <div style={{background:"linear-gradient(to bottom,rgba(5,14,28,0.6),transparent)",height:5,borderRadius:"0 0 50px 50px",width:"108%",marginLeft:"-4%"}}/>
    </div>
  );
}

/* ══════════════════════════════════════════
   PRIVACY CENTRE PAGE
══════════════════════════════════════════ */
function PrivacyCentre({ onNav }) {
  return (
    <div style={{minHeight:"100vh",background:"#f8fafc",fontFamily:"'Inter',sans-serif"}}>
      <div style={{background:"#0b1628",padding:"0 48px",height:64,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:100}}>
        <button onClick={()=>onNav("home")} style={{background:"none",border:"none",color:"white",cursor:"pointer",display:"flex",alignItems:"center",gap:8,fontSize:14,fontWeight:600,fontFamily:"'Inter',sans-serif",padding:0}}>
          <I.ChevL s={16} c="white"/> Diswayne Tech Systems
        </button>
        <span style={{color:"rgba(255,255,255,0.4)",fontSize:12}}>Privacy Centre</span>
      </div>
      <div style={{maxWidth:720,margin:"0 auto",padding:"56px 32px"}}>
        <div style={{display:"flex",alignItems:"center",gap:14,marginBottom:8}}>
          <div style={{width:44,height:44,borderRadius:11,background:"#0b1628",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <I.Shield s={20} c="#00d4ff"/>
          </div>
          <h1 style={{fontSize:28,fontWeight:800,color:"#1a2540",margin:0}}>Privacy Centre</h1>
        </div>
        <p style={{fontSize:15,color:"#64748b",lineHeight:1.75,marginBottom:40,marginTop:16}}>
          At Diswayne Tech Systems (Pty) Ltd, privacy is built into everything we create. Below you will find clear information on how each of our apps handles your personal data.
        </p>
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:20,marginBottom:40}}>
          <button onClick={()=>onNav("privacy-lunacycle")} style={{background:"linear-gradient(135deg,#1a0825,#2d1060)",borderRadius:14,padding:24,border:"none",cursor:"pointer",textAlign:"left"}}>
            <div style={{width:32,height:32,borderRadius:"50%",background:"linear-gradient(135deg,#a855f7,#ec4899)",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:12}}>
              <I.Moon s={14} c="white"/>
            </div>
            <div style={{color:"white",fontWeight:700,fontSize:17,marginBottom:6}}>LunaCycle</div>
            <div style={{color:"rgba(255,255,255,0.5)",fontSize:12,lineHeight:1.6,marginBottom:14}}>Menstrual cycle tracking with Firebase sync and secure account management.</div>
            <div style={{color:"#a855f7",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:5}}>
              View Privacy Policy <I.Arrow s={12} c="#a855f7"/>
            </div>
          </button>
          <button onClick={()=>onNav("privacy-splitease")} style={{background:"linear-gradient(135deg,#04090f,#091624)",borderRadius:14,padding:24,border:"1px solid rgba(13,148,136,0.25)",cursor:"pointer",textAlign:"left"}}>
            <div style={{width:32,height:32,borderRadius:8,background:"linear-gradient(135deg,#0d9488,#0891b2)",display:"flex",alignItems:"center",justifyContent:"center",marginBottom:12}}>
              <I.Dollar s={14} c="white"/>
            </div>
            <div style={{color:"white",fontWeight:700,fontSize:17,marginBottom:6}}>SplitEase</div>
            <div style={{color:"rgba(255,255,255,0.5)",fontSize:12,lineHeight:1.6,marginBottom:14}}>Bill splitting — zero data collected, everything stays on your device.</div>
            <div style={{color:"#0d9488",fontSize:12,fontWeight:600,display:"flex",alignItems:"center",gap:5}}>
              View Privacy Policy <I.Arrow s={12} c="#0d9488"/>
            </div>
          </button>
        </div>
        <div style={{background:"white",borderRadius:10,padding:20,border:"1px solid #e2e8f0",fontSize:14,color:"#64748b",lineHeight:1.7}}>
          For privacy-related requests contact us at{" "}
          <a href="mailto:support@getdiswayne.com" style={{color:"#2d7cf6",textDecoration:"none",fontWeight:500}}>support@getdiswayne.com</a>.{" "}
          Diswayne Tech Systems (Pty) Ltd is registered in South Africa and complies with the Protection of Personal Information Act (POPIA).
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   LUNACYCLE PRIVACY PAGE
══════════════════════════════════════════ */
function LunaPrivacy({ onNav }) {
  const S = ({ children }) => <section style={{marginBottom:36}}>{children}</section>;
  const H2 = ({ children }) => <h2 style={{fontSize:17,fontWeight:700,color:"#1a2540",marginBottom:10,marginTop:0,paddingBottom:8,borderBottom:"1px solid #e2e8f0"}}>{children}</h2>;
  const P = ({ children }) => <p style={{fontSize:14,color:"#475569",lineHeight:1.8,marginBottom:10,marginTop:0}}>{children}</p>;
  const Li = ({ items }) => (
    <ul style={{margin:"0 0 12px 0",padding:"0 0 0 18px",display:"flex",flexDirection:"column",gap:5}}>
      {items.map((it,i)=><li key={i} style={{fontSize:14,color:"#475569",lineHeight:1.7}}>{it}</li>)}
    </ul>
  );
  const ac = "#a855f7";
  return (
    <div style={{minHeight:"100vh",background:"#f8fafc",fontFamily:"'Inter',sans-serif"}}>
      <div style={{background:"linear-gradient(135deg,#1a0825,#2d1060)",padding:"0 48px",position:"sticky",top:0,zIndex:100}}>
        <div style={{maxWidth:760,margin:"0 auto",height:64,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <button onClick={()=>onNav("privacy")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.65)",cursor:"pointer",display:"flex",alignItems:"center",gap:7,fontSize:13,fontFamily:"'Inter',sans-serif",padding:0}}>
            <I.ChevL s={15} c="rgba(255,255,255,0.65)"/> Privacy Centre
          </button>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{width:20,height:20,borderRadius:"50%",background:"linear-gradient(135deg,#a855f7,#ec4899)",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <I.Moon s={9} c="white"/>
            </div>
            <span style={{color:"white",fontSize:13,fontWeight:600}}>LunaCycle Privacy Policy</span>
          </div>
        </div>
      </div>
      <div style={{maxWidth:760,margin:"0 auto",padding:"48px 32px"}}>
        <div style={{background:"linear-gradient(135deg,#1a0825,#2d1060)",borderRadius:14,padding:"28px 32px",marginBottom:40,display:"flex",alignItems:"center",gap:20}}>
          <div style={{width:48,height:48,borderRadius:"50%",background:"linear-gradient(135deg,#a855f7,#ec4899)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <I.Moon s={22} c="white"/>
          </div>
          <div>
            <h1 style={{fontSize:22,fontWeight:800,color:"white",margin:"0 0 4px 0"}}>LunaCycle Privacy Policy</h1>
            <p style={{margin:0,color:"rgba(255,255,255,0.45)",fontSize:13}}>Last updated: 26 June 2026 · Effective: 26 June 2026</p>
          </div>
        </div>
        <S><H2>1. Introduction</H2>
          <P>LunaCycle is a menstrual cycle and reproductive health tracking application developed and operated by Diswayne Tech Systems (Pty) Ltd ("we", "us", "our"). We are committed to protecting your privacy and handling your personal information — including sensitive health data — with care and full transparency.</P>
          <P>This Policy explains what information we collect, how we use and store it, and your rights under the Protection of Personal Information Act 4 of 2013 (POPIA) and other applicable privacy laws.</P>
        </S>
        <S><H2>2. Who We Are</H2>
          <P><strong>Data Controller:</strong> Diswayne Tech Systems (Pty) Ltd</P>
          <P><strong>Country:</strong> South Africa</P>
          <P><strong>Email:</strong> <a href="mailto:support@getdiswayne.com" style={{color:ac}}>support@getdiswayne.com</a></P>
          <P><strong>Website:</strong> <a href="https://getdiswayne.com" style={{color:ac}}>getdiswayne.com</a></P>
        </S>
        <S><H2>3. Data We Collect</H2>
          <P><strong>3.1 Account Information</strong> — Collected when you register:</P>
          <Li items={["Email address (your login identifier)","Password (hashed and managed by Firebase Authentication — we never store or see your plain-text password)","Optional display name"]}/>
          <P><strong>3.2 Health &amp; Cycle Data</strong> — This constitutes special personal information under POPIA and is collected only with your explicit consent:</P>
          <Li items={["Menstrual cycle start and end dates","Flow intensity (light, medium, heavy)","Symptoms you choose to log (cramping, bloating, mood, energy levels, headaches, and others)","Ovulation and fertile window tracking data","Personal notes added to any calendar day","Predicted future cycle dates based on your logged history"]}/>
          <P><strong>3.3 Device &amp; Technical Data</strong></P>
          <Li items={["Device operating system and version","App version number"]}/>
          <P>We do not use third-party analytics tools and we do not collect advertising identifiers.</P>
        </S>
        <S><H2>4. Why We Collect Your Data</H2>
          <Li items={["To provide cycle tracking, predictions, and health insights within the app","To synchronise your data securely across your devices via your account","To allow you to view your historical cycle data and trends","To maintain your account and respond to support requests"]}/>
        </S>
        <S><H2>5. Legal Basis for Processing</H2>
          <Li items={["Account and technical data: Performance of a contract (providing you with the app service)","Health data: Your explicit consent, given at registration and on first use of health logging features. You may withdraw consent at any time by deleting your account."]}/>
        </S>
        <S><H2>6. Firebase &amp; Google as Data Processor</H2>
          <P>LunaCycle uses Google Firebase for authentication (Firebase Auth) and cloud data storage (Firestore). Firebase processes your data on our behalf as our data processor, under our instructions and subject to Google's data processing terms.</P>
          <P>Firebase infrastructure may be hosted outside South Africa (primarily the United States and/or Europe). By using LunaCycle you consent to this transfer. Google maintains appropriate safeguards including encryption and compliance with international data protection frameworks. See <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" style={{color:ac}}>Firebase Privacy information</a> for full details.</P>
        </S>
        <S><H2>7. Data Security</H2>
          <Li items={["All data in transit is encrypted using TLS/HTTPS","Data at rest in Firebase Firestore is encrypted by Google","Firebase Authentication manages password security — your password is never stored in plain text","Access to your data is restricted to your account only","We review our security practices regularly"]}/>
          <P>No system is entirely risk-free. We encourage you to use a strong, unique password and to sign out on shared devices.</P>
        </S>
        <S><H2>8. Data Retention</H2>
          <P>Your data is retained for as long as your account is active. When you delete your account through the app settings, all associated personal information — including all health and cycle data — is permanently deleted from our systems within 30 days. You may also delete individual data entries at any time from within the app.</P>
        </S>
        <S><H2>9. Your Rights Under POPIA</H2>
          <P>As a data subject under South African law you have the right to:</P>
          <Li items={["Access: Request a copy of the personal information we hold about you","Correction: Request correction of inaccurate or incomplete data","Deletion: Request permanent deletion of your personal information","Objection: Object to certain processing of your information","Complaint: Lodge a complaint with the Information Regulator of South Africa"]}/>
          <P>To exercise any of these rights, contact us at <a href="mailto:support@getdiswayne.com" style={{color:ac}}>support@getdiswayne.com</a>. We will respond within 30 days.</P>
          <P><strong>Information Regulator (South Africa):</strong> <a href="https://www.inforegulator.org.za" target="_blank" rel="noopener noreferrer" style={{color:ac}}>inforegulator.org.za</a></P>
        </S>
        <S><H2>10. Children &amp; Minors</H2>
          <P>LunaCycle is intended for users aged 18 years and older. We do not knowingly collect personal information from anyone under 18. If you believe a minor has created an account, contact us immediately at <a href="mailto:support@getdiswayne.com" style={{color:ac}}>support@getdiswayne.com</a> and we will delete the account and all associated data promptly.</P>
        </S>
        <S><H2>11. Changes to This Policy</H2>
          <P>We may update this Privacy Policy from time to time. When we do, we will update the "Last updated" date and notify you through the app. Continued use of LunaCycle after changes are published constitutes your acceptance of the updated policy.</P>
        </S>
        <S><H2>12. Contact Us</H2>
          <P>Diswayne Tech Systems (Pty) Ltd</P>
          <P>Email: <a href="mailto:support@getdiswayne.com" style={{color:ac}}>support@getdiswayne.com</a></P>
          <P>Website: <a href="https://getdiswayne.com" style={{color:ac}}>getdiswayne.com</a></P>
        </S>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   SPLITEASE PRIVACY PAGE
══════════════════════════════════════════ */
function SplitPrivacy({ onNav }) {
  const S = ({ children }) => <section style={{marginBottom:36}}>{children}</section>;
  const H2 = ({ children }) => <h2 style={{fontSize:17,fontWeight:700,color:"#1a2540",marginBottom:10,marginTop:0,paddingBottom:8,borderBottom:"1px solid #e2e8f0"}}>{children}</h2>;
  const P = ({ children }) => <p style={{fontSize:14,color:"#475569",lineHeight:1.8,marginBottom:10,marginTop:0}}>{children}</p>;
  const ac = "#0d9488";
  return (
    <div style={{minHeight:"100vh",background:"#f8fafc",fontFamily:"'Inter',sans-serif"}}>
      <div style={{background:"linear-gradient(135deg,#04090f,#091624)",padding:"0 48px",position:"sticky",top:0,zIndex:100,borderBottom:"1px solid rgba(13,148,136,0.2)"}}>
        <div style={{maxWidth:760,margin:"0 auto",height:64,display:"flex",alignItems:"center",justifyContent:"space-between"}}>
          <button onClick={()=>onNav("privacy")} style={{background:"none",border:"none",color:"rgba(255,255,255,0.6)",cursor:"pointer",display:"flex",alignItems:"center",gap:7,fontSize:13,fontFamily:"'Inter',sans-serif",padding:0}}>
            <I.ChevL s={15} c="rgba(255,255,255,0.6)"/> Privacy Centre
          </button>
          <div style={{display:"flex",alignItems:"center",gap:8}}>
            <div style={{width:20,height:20,borderRadius:6,background:"linear-gradient(135deg,#0d9488,#0891b2)",display:"flex",alignItems:"center",justifyContent:"center"}}>
              <I.Dollar s={9} c="white"/>
            </div>
            <span style={{color:"white",fontSize:13,fontWeight:600}}>SplitEase Privacy Policy</span>
          </div>
        </div>
      </div>
      <div style={{maxWidth:760,margin:"0 auto",padding:"48px 32px"}}>
        <div style={{background:"linear-gradient(135deg,#04090f,#091624)",borderRadius:14,padding:"28px 32px",marginBottom:40,display:"flex",alignItems:"flex-start",gap:20,border:"1px solid rgba(13,148,136,0.25)"}}>
          <div style={{width:48,height:48,borderRadius:10,background:"linear-gradient(135deg,#0d9488,#0891b2)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
            <I.Shield s={22} c="white"/>
          </div>
          <div>
            <h1 style={{fontSize:22,fontWeight:800,color:"white",margin:"0 0 4px 0"}}>SplitEase Privacy Policy</h1>
            <p style={{margin:"0 0 14px 0",color:"rgba(255,255,255,0.45)",fontSize:13}}>Last updated: 26 June 2026</p>
            <div style={{background:"rgba(13,148,136,0.2)",borderRadius:7,padding:"9px 14px",display:"inline-block"}}>
              <span style={{color:"#0d9488",fontWeight:700,fontSize:13}}>✓ No data collected. No accounts. No tracking.</span>
            </div>
          </div>
        </div>
        <S><H2>1. Overview</H2>
          <P>SplitEase is a bill-splitting calculator developed by Diswayne Tech Systems (Pty) Ltd. SplitEase does not collect, store, transmit, or share any personal information. This policy exists to confirm that fact and give you complete transparency.</P>
        </S>
        <S><H2>2. How the App Works</H2>
          <P>All calculations happen entirely on your device in the app's runtime memory. When you close the app, all data is cleared automatically. There is no backend server, no database, and no cloud storage of any kind.</P>
        </S>
        <S><H2>3. No Account Required</H2>
          <P>You do not need to create an account, provide an email address, or share any personal information to use SplitEase.</P>
        </S>
        <S><H2>4. No Data Transmitted</H2>
          <P>SplitEase does not transmit any data over the internet. The app does not require an internet connection for any of its core features. Nothing you enter is ever sent to our servers or any third party.</P>
        </S>
        <S><H2>5. Device Permissions</H2>
          <P>SplitEase does not request access to any sensitive device permissions. No access to contacts, location, camera, microphone, or health data is required or requested.</P>
        </S>
        <S><H2>6. Third-Party Services</H2>
          <P>SplitEase does not integrate any advertising networks, analytics services, or third-party SDKs that collect user data.</P>
        </S>
        <S><H2>7. Children</H2>
          <P>Because SplitEase collects no personal information whatsoever, it is appropriate for all ages.</P>
        </S>
        <S><H2>8. Changes to This Policy</H2>
          <P>If we ever materially change this policy — for example, if a future version introduces features requiring data collection — we will update this page and notify users through the app with full transparency and user control.</P>
        </S>
        <S><H2>9. Contact Us</H2>
          <P>Diswayne Tech Systems (Pty) Ltd</P>
          <P>Email: <a href="mailto:support@getdiswayne.com" style={{color:ac}}>support@getdiswayne.com</a></P>
          <P>Website: <a href="https://getdiswayne.com" style={{color:ac}}>getdiswayne.com</a></P>
        </S>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN APP
══════════════════════════════════════════ */
export default function App() {
  const [navOpen, setNavOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [formData, setFormData] = useState({ name:"", email:"", service:"", message:"" });
  const [formSent, setFormSent] = useState(false);
  const [sending, setSending] = useState(false);

  const getPage = () => {
    const h = window.location.hash;
    if (h === "#/privacy") return "privacy";
    if (h === "#/privacy-lunacycle") return "privacy-lunacycle";
    if (h === "#/privacy-splitease") return "privacy-splitease";
    return "home";
  };
  const [page, setPage] = useState(getPage);

  const onNav = (p) => {
    if (p === "home") { window.location.hash = ""; }
    else { window.location.hash = `/${p}`; }
    setPage(p);
    window.scrollTo({ top:0, behavior:"smooth" });
  };

  useEffect(() => {
    const handler = () => {
      const h = window.location.hash;
      if (h === "#/privacy") setPage("privacy");
      else if (h === "#/privacy-lunacycle") setPage("privacy-lunacycle");
      else if (h === "#/privacy-splitease") setPage("privacy-splitease");
      else if (!h.startsWith("#/")) setPage("home");
    };
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", handler, { passive: true });
    return () => window.removeEventListener("scroll", handler);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    await new Promise(r => setTimeout(r, 1400));
    setFormSent(true);
    setSending(false);
  };

  if (page === "privacy") return <PrivacyCentre onNav={onNav}/>;
  if (page === "privacy-lunacycle") return <LunaPrivacy onNav={onNav}/>;
  if (page === "privacy-splitease") return <SplitPrivacy onNav={onNav}/>;

  const services = [
    { Icon:I.Code,     title:"Custom Software",     desc:"End-to-end development tailored to your exact business logic — no templates, no shortcuts.",          accent:"#2d7cf6", tags:["React","Node.js","Python","REST APIs"] },
    { Icon:I.Layers,   title:"SaaS Products",       desc:"Multi-tenant platforms built to scale from day one, with subscription management and clean architecture.", accent:"#6366f1", tags:["Architecture","Billing","Auth","CI/CD"] },
    { Icon:I.Globe,    title:"Website Development", desc:"Fast, modern, accessible websites that convert visitors and rank — designed and built in-house.",        accent:"#0891b2", tags:["Next.js","Tailwind","SEO","CMS"] },
    { Icon:I.Smartphone,title:"Mobile Apps",        desc:"Native iOS and Android experiences that feel right on the device, built with React Native.",             accent:"#7c3aed", tags:["React Native","iOS","Android","Expo"] },
    { Icon:I.Tool,     title:"Maintenance & Support",desc:"Ongoing care plans to keep your software secure, updated, and performing — so you can focus on growth.", accent:"#0d9488", tags:["Monitoring","Updates","Uptime","SLA"] },
    { Icon:I.Cpu,      title:"AI Integration",      desc:"Practical AI features woven into your product — LLMs, automation pipelines, and intelligent workflows.", accent:"#00d4ff", tags:["OpenAI","LangChain","Automation","RAG"] },
    { Icon:I.Server,   title:"Backend & Infrastructure",desc:"Robust APIs, databases, and cloud infrastructure engineered for reliability and performance at scale.", accent:"#2d7cf6", tags:["AWS","Firebase","PostgreSQL","Docker"] },
  ];

  const whyUs = [
    { Icon:I.Code,   title:"We Write Real Code",   desc:"No drag-and-drop builders or no-code shortcuts. Every solution is engineered to your specific needs.",          accent:"#2d7cf6" },
    { Icon:I.Shield, title:"Security First",        desc:"Security is not a feature we add at the end — it is designed in from the first line of code.",                  accent:"#6366f1" },
    { Icon:I.Zap,    title:"Delivery That Sticks",  desc:"We deliver on time with proper documentation and handover, so you are never left dependent on us to function.", accent:"#00d4ff" },
    { Icon:I.Users,  title:"Direct Communication",  desc:"You work directly with the developers building your product — no account managers, no telephone game.",          accent:"#0d9488" },
  ];

  const steps = [
    { n:"01", title:"Discovery",   desc:"We learn your business, goals, and constraints. No assumptions." },
    { n:"02", title:"Scoping",     desc:"A clear, agreed specification and timeline before any code is written." },
    { n:"03", title:"Build",       desc:"Iterative development with regular demos and your feedback built in." },
    { n:"04", title:"Launch",      desc:"Smooth deployment, documentation, and a handover you actually understand." },
  ];

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        :root {
          --navy: #0b1628; --navy-mid: #102040; --navy-light: #1a3055;
          --blue: #1e5fc2; --blue-light: #2d7cf6; --accent: #00d4ff;
          --white: #ffffff; --off: #f4f7fb; --text: #1a2540;
          --muted: #64748b; --border: #e2e8f0;
          --font: 'Inter', system-ui, sans-serif;
          --r: 14px; --r-sm: 9px;
          --sh: 0 4px 24px rgba(11,22,40,0.08);
          --sh-lg: 0 12px 48px rgba(11,22,40,0.14);
        }
        html { scroll-behavior: smooth; }
        body { font-family: var(--font); color: var(--text); background: #fff; -webkit-font-smoothing: antialiased; }
        a { text-decoration: none; color: inherit; }
        button { font-family: var(--font); }
        img, svg { display: block; }

        /* ── NAV ─────────────────────────────── */
        .nav {
          position: fixed; top: 0; left: 0; right: 0; z-index: 200;
          height: 68px; display: flex; align-items: center;
          padding: 0 48px; justify-content: space-between;
          transition: background 0.3s, backdrop-filter 0.3s, box-shadow 0.3s;
        }
        .nav.scrolled {
          background: rgba(11,22,40,0.92);
          backdrop-filter: blur(14px);
          box-shadow: 0 1px 0 rgba(255,255,255,0.06);
        }
        .nav-logo {
          font-size: 17px; font-weight: 800; color: #fff;
          letter-spacing: -0.3px; display: flex; align-items: center; gap: 10px;
        }
        .nav-logo-dot { width: 8px; height: 8px; border-radius: 50%; background: var(--accent); }
        .nav-links { display: flex; align-items: center; gap: 6px; }
        .nav-links a {
          color: rgba(255,255,255,0.7); font-size: 14px; font-weight: 500;
          padding: 7px 14px; border-radius: 8px; transition: all 0.18s;
        }
        .nav-links a:hover { color: #fff; background: rgba(255,255,255,0.08); }
        .nav-cta {
          background: var(--blue-light) !important; color: #fff !important;
          padding: 8px 20px !important; border-radius: 9px !important; font-weight: 600 !important;
          margin-left: 6px; transition: opacity 0.18s !important;
        }
        .nav-cta:hover { opacity: 0.88 !important; background: var(--blue-light) !important; }
        .nav-burger { display: none; background: none; border: none; cursor: pointer; padding: 6px; color: white; }
        .mobile-nav {
          display: none; position: fixed; top: 68px; left: 0; right: 0; z-index: 199;
          background: rgba(11,22,40,0.97); backdrop-filter: blur(14px);
          padding: 12px 24px 24px; flex-direction: column; gap: 4px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .mobile-nav.open { display: flex; }
        .mobile-nav a {
          color: rgba(255,255,255,0.75); font-size: 15px; font-weight: 500;
          padding: 12px 16px; border-radius: 9px; transition: all 0.15s;
        }
        .mobile-nav a:hover { background: rgba(255,255,255,0.08); color: white; }

        /* ── HERO ────────────────────────────── */
        .hero {
          min-height: 100vh; background: var(--navy);
          display: flex; align-items: center; justify-content: center;
          padding: 120px 48px 80px; position: relative; overflow: hidden;
        }
        .hero-grid {
          position: absolute; inset: 0; pointer-events: none;
          background-image:
            linear-gradient(rgba(45,124,246,0.07) 1px, transparent 1px),
            linear-gradient(90deg, rgba(45,124,246,0.07) 1px, transparent 1px);
          background-size: 56px 56px;
        }
        .hero-glow {
          position: absolute; top: -20%; right: -10%; width: 60%; height: 70%;
          background: radial-gradient(ellipse, rgba(45,124,246,0.18) 0%, transparent 70%);
          pointer-events: none;
        }
        .hero-glow2 {
          position: absolute; bottom: -10%; left: 5%; width: 40%; height: 50%;
          background: radial-gradient(ellipse, rgba(0,212,255,0.09) 0%, transparent 65%);
          pointer-events: none;
        }
        .hero-inner {
          max-width: 1200px; width: 100%; margin: 0 auto;
          display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: center; position: relative; z-index: 1;
        }
        .hero-badge {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(45,124,246,0.15); border: 1px solid rgba(45,124,246,0.3);
          border-radius: 50px; padding: 6px 14px; margin-bottom: 24px;
        }
        .hero-badge-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--accent); animation: pulse 2s infinite; }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.4} }
        .hero-badge-text { font-size: 12px; font-weight: 600; color: var(--accent); text-transform: uppercase; letter-spacing: 1px; }
        .hero h1 {
          font-size: clamp(38px, 5vw, 58px); font-weight: 900; color: #fff;
          line-height: 1.08; letter-spacing: -1.5px; margin-bottom: 20px;
        }
        .hero h1 span { color: var(--accent); }
        .hero-sub { font-size: 17px; color: rgba(255,255,255,0.6); line-height: 1.7; margin-bottom: 36px; max-width: 480px; }
        .hero-btns { display: flex; gap: 12px; flex-wrap: wrap; }
        .btn-primary {
          display: inline-flex; align-items: center; gap: 8px;
          background: var(--blue-light); color: #fff; font-size: 15px; font-weight: 700;
          padding: 13px 28px; border-radius: 10px; border: none; cursor: pointer;
          transition: opacity 0.18s, transform 0.18s; text-decoration: none;
        }
        .btn-primary:hover { opacity: 0.88; transform: translateY(-1px); }
        .btn-outline {
          display: inline-flex; align-items: center; gap: 8px;
          background: rgba(255,255,255,0.07); color: rgba(255,255,255,0.85);
          font-size: 15px; font-weight: 600; padding: 13px 28px; border-radius: 10px;
          border: 1px solid rgba(255,255,255,0.14); text-decoration: none; transition: all 0.18s;
        }
        .btn-outline:hover { background: rgba(255,255,255,0.12); }
        .hero-float {
          background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1);
          backdrop-filter: blur(12px); border-radius: 18px; padding: 28px 24px;
        }
        .hero-float-title { font-size: 11px; font-weight: 700; color: rgba(255,255,255,0.4); text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 18px; }
        .hero-float-item {
          display: flex; align-items: flex-start; gap: 14px;
          padding: 12px 0; border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .hero-float-item:last-child { border-bottom: none; padding-bottom: 0; }
        .hero-float-icon {
          width: 36px; height: 36px; border-radius: 9px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
        }
        .hero-float-item-title { font-size: 13px; font-weight: 700; color: #fff; margin-bottom: 2px; }
        .hero-float-item-sub { font-size: 11px; color: rgba(255,255,255,0.45); line-height: 1.5; }
        .hero-stats {
          grid-column: 1 / -1; display: flex; gap: 48px;
          padding-top: 40px; border-top: 1px solid rgba(255,255,255,0.08);
          flex-wrap: wrap;
        }
        .hero-stat-num { font-size: 28px; font-weight: 900; color: #fff; letter-spacing: -0.5px; }
        .hero-stat-label { font-size: 13px; color: rgba(255,255,255,0.45); margin-top: 2px; }

        /* ── SECTIONS ────────────────────────── */
        .section { padding: 96px 48px; }
        .section-inner { max-width: 1200px; margin: 0 auto; }
        .section-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; color: var(--blue-light); margin-bottom: 10px; }
        .section-title { font-size: clamp(28px, 4vw, 40px); font-weight: 800; color: var(--text); letter-spacing: -0.5px; margin-bottom: 14px; line-height: 1.15; }
        .section-sub { font-size: 16px; color: var(--muted); line-height: 1.7; max-width: 560px; }

        /* ── SERVICES ────────────────────────── */
        .services-bg { background: var(--off); }
        .svc-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; margin-top: 56px; }
        .svc-card {
          background: #fff; border: 1px solid var(--border); border-radius: var(--r);
          padding: 28px; transition: box-shadow 0.22s, transform 0.22s;
        }
        .svc-card:hover { box-shadow: var(--sh-lg); transform: translateY(-3px); }
        .svc-icon {
          width: 46px; height: 46px; border-radius: 11px;
          display: flex; align-items: center; justify-content: center; margin-bottom: 18px;
        }
        .svc-title { font-size: 16px; font-weight: 700; margin-bottom: 8px; }
        .svc-desc { font-size: 13px; color: var(--muted); line-height: 1.65; margin-bottom: 16px; }
        .svc-tags { display: flex; flex-wrap: wrap; gap: 6px; }
        .svc-tag {
          font-size: 11px; font-weight: 600; padding: 3px 9px; border-radius: 50px;
          background: var(--off); color: var(--muted); border: 1px solid var(--border);
        }

        /* ── APPS SECTION ────────────────────── */
        .apps-wrap { background: var(--navy); padding: 96px 48px; }
        .apps-inner { max-width: 1200px; margin: 0 auto; }
        .apps-header { margin-bottom: 72px; }
        .apps-header .section-label { color: var(--accent); }
        .apps-header .section-title { color: #fff; }
        .apps-header .section-sub { color: rgba(255,255,255,0.55); }
        .app-row {
          display: grid; grid-template-columns: 1fr 1fr; gap: 72px; align-items: center;
          padding-bottom: 72px; margin-bottom: 72px;
          border-bottom: 1px solid rgba(255,255,255,0.06);
        }
        .app-row:last-child { border-bottom: none; padding-bottom: 0; margin-bottom: 0; }
        .app-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 2px; margin-bottom: 10px; }
        .app-name { font-size: clamp(30px, 4vw, 44px); font-weight: 900; color: #fff; line-height: 1.05; letter-spacing: -1px; margin-bottom: 8px; }
        .app-tagline { font-size: 15px; font-weight: 500; margin-bottom: 16px; }
        .app-desc { font-size: 15px; color: rgba(255,255,255,0.6); line-height: 1.75; margin-bottom: 24px; }
        .app-feats { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; margin-bottom: 28px; }
        .app-feat { font-size: 13px; color: rgba(255,255,255,0.65); display: flex; align-items: center; gap: 8px; }
        .app-feat-dot { width: 5px; height: 5px; border-radius: 50%; flex-shrink: 0; }
        .app-actions { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; }
        .store-btn {
          display: inline-flex; align-items: center; gap: 10px;
          background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12);
          border-radius: 9px; padding: 9px 16px; cursor: pointer; transition: all 0.18s;
        }
        .store-btn:hover { background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.2); }
        .store-btn-sm { color: rgba(255,255,255,0.4); font-size: 9px; font-weight: 500; }
        .store-btn-name { color: #fff; font-size: 13px; font-weight: 700; }
        .privacy-pill {
          font-size: 12px; color: rgba(255,255,255,0.35); background: none; border: none;
          cursor: pointer; padding: 0; font-family: var(--font); text-decoration: underline;
          transition: color 0.15s;
        }
        .privacy-pill:hover { color: rgba(255,255,255,0.65); }
        .mockup-stage { display: flex; align-items: flex-end; }
        .mockup-phone-wrap { position: relative; z-index: 2; margin-right: -48px; filter: drop-shadow(0 20px 48px rgba(0,0,0,0.5)); }
        .mockup-desktop-wrap { filter: drop-shadow(0 16px 40px rgba(0,0,0,0.35)); }
        .mockup-stage-rev { display: flex; align-items: flex-end; flex-direction: row-reverse; }
        .mockup-stage-rev .mockup-phone-wrap { margin-right: 0; margin-left: -48px; }

        /* ── WHY US ──────────────────────────── */
        .why-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; margin-top: 56px; }
        .why-card {
          background: #fff; border: 1px solid var(--border); border-radius: var(--r);
          padding: 32px; display: flex; gap: 20px; transition: box-shadow 0.22s;
        }
        .why-card:hover { box-shadow: var(--sh-lg); }
        .why-icon { width: 44px; height: 44px; border-radius: 11px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .why-title { font-size: 16px; font-weight: 700; margin-bottom: 8px; }
        .why-desc { font-size: 14px; color: var(--muted); line-height: 1.65; }

        /* ── PROCESS ─────────────────────────── */
        .process-bg { background: var(--navy); }
        .process-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 1px; background: rgba(255,255,255,0.06); border: 1px solid rgba(255,255,255,0.06); border-radius: var(--r); overflow: hidden; margin-top: 56px; }
        .process-step { background: var(--navy); padding: 32px 28px; position: relative; }
        .process-num { font-size: 42px; font-weight: 900; color: rgba(45,124,246,0.2); letter-spacing: -2px; line-height: 1; margin-bottom: 16px; font-variant-numeric: tabular-nums; }
        .process-title { font-size: 16px; font-weight: 700; color: #fff; margin-bottom: 8px; }
        .process-desc { font-size: 13px; color: rgba(255,255,255,0.5); line-height: 1.65; }
        .process-bg .section-title { color: #fff; }
        .process-bg .section-sub { color: rgba(255,255,255,0.55); }

        /* ── CONTACT ─────────────────────────── */
        .contact-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 72px; margin-top: 56px; align-items: start; }
        .contact-info-item { display: flex; gap: 14px; align-items: flex-start; margin-bottom: 24px; }
        .contact-info-icon { width: 40px; height: 40px; border-radius: 10px; background: rgba(45,124,246,0.1); display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .contact-info-label { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; color: var(--muted); margin-bottom: 2px; }
        .contact-info-value { font-size: 15px; font-weight: 600; color: var(--text); }
        .form-group { margin-bottom: 18px; }
        .form-label { font-size: 13px; font-weight: 600; color: var(--text); display: block; margin-bottom: 7px; }
        .form-input {
          width: 100%; padding: 11px 14px; font-size: 14px; font-family: var(--font);
          border: 1.5px solid var(--border); border-radius: var(--r-sm);
          background: #fff; color: var(--text); outline: none; transition: border-color 0.18s;
        }
        .form-input:focus { border-color: var(--blue-light); }
        textarea.form-input { resize: vertical; min-height: 120px; }
        .form-submit {
          width: 100%; padding: 13px; background: var(--blue-light); color: #fff;
          font-size: 15px; font-weight: 700; font-family: var(--font);
          border: none; border-radius: 10px; cursor: pointer; transition: opacity 0.18s;
        }
        .form-submit:hover { opacity: 0.88; }
        .form-submit:disabled { opacity: 0.6; cursor: not-allowed; }
        .success-box {
          background: rgba(13,148,136,0.08); border: 1px solid rgba(13,148,136,0.25);
          border-radius: var(--r); padding: 28px; text-align: center;
        }
        .success-icon { width: 48px; height: 48px; border-radius: 50%; background: rgba(13,148,136,0.15); display: flex; align-items: center; justify-content: center; margin: 0 auto 14px; }
        .success-title { font-size: 18px; font-weight: 700; color: var(--text); margin-bottom: 6px; }
        .success-sub { font-size: 14px; color: var(--muted); }

        /* ── FOOTER ──────────────────────────── */
        .footer { background: var(--navy); padding: 48px; border-top: 1px solid rgba(255,255,255,0.06); }
        .footer-inner { max-width: 1200px; margin: 0 auto; display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 24px; }
        .footer-brand { display: flex; align-items: center; gap: 10px; }
        .footer-brand-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--accent); }
        .footer-brand-name { font-size: 14px; font-weight: 700; color: #fff; }
        .footer-links { display: flex; gap: 6px; flex-wrap: wrap; }
        .footer-links a, .footer-privacy-btn {
          font-size: 13px; color: rgba(255,255,255,0.45); padding: 5px 10px; border-radius: 6px;
          transition: color 0.15s; background: none; border: none; cursor: pointer; font-family: var(--font);
        }
        .footer-links a:hover, .footer-privacy-btn:hover { color: rgba(255,255,255,0.8); }
        .footer-copy { font-size: 12px; color: rgba(255,255,255,0.28); width: 100%; text-align: center; margin-top: 12px; padding-top: 20px; border-top: 1px solid rgba(255,255,255,0.05); }

        /* ── RESPONSIVE ──────────────────────── */
        @media (max-width: 960px) {
          .nav { padding: 0 24px; }
          .nav-links { display: none; }
          .nav-burger { display: flex; }
          .hero { padding: 100px 24px 64px; }
          .hero-inner { grid-template-columns: 1fr; gap: 40px; }
          .hero-float { display: none; }
          .hero-stats { gap: 32px; }
          .section { padding: 72px 24px; }
          .svc-grid { grid-template-columns: 1fr 1fr; }
          .apps-wrap { padding: 72px 24px; }
          .app-row { grid-template-columns: 1fr; gap: 40px; }
          .mockup-stage, .mockup-stage-rev { justify-content: center; flex-direction: column; align-items: center; gap: 20px; }
          .mockup-phone-wrap { margin-right: 0 !important; margin-left: 0 !important; }
          .why-grid { grid-template-columns: 1fr; }
          .process-grid { grid-template-columns: 1fr 1fr; }
          .contact-grid { grid-template-columns: 1fr; gap: 40px; }
          .footer { padding: 32px 24px; }
          .footer-inner { flex-direction: column; align-items: flex-start; gap: 16px; }
        }
        @media (max-width: 600px) {
          .hero h1 { font-size: 34px; }
          .svc-grid { grid-template-columns: 1fr; }
          .app-feats { grid-template-columns: 1fr; }
          .process-grid { grid-template-columns: 1fr; }
          .hero-stats { flex-direction: column; gap: 20px; }
          .apps-wrap { padding: 60px 20px; }
        }
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after { transition-duration: 0.01ms !important; animation-duration: 0.01ms !important; }
        }
      `}</style>

      {/* ── NAV ── */}
      <nav className={`nav${scrolled ? " scrolled" : ""}`}>
        <a href="#" className="nav-logo">
          <div className="nav-logo-dot"/>
          Diswayne Tech Systems
        </a>
        <div className="nav-links">
          <a href="#services">Services</a>
          <a href="#apps">Apps</a>
          <a href="#about">About</a>
          <a href="#contact" className="nav-cta">Get In Touch</a>
        </div>
        <button className="nav-burger" onClick={()=>setNavOpen(o=>!o)} aria-label="Toggle menu">
          {navOpen ? <I.X s={22} c="white"/> : <I.Menu s={22} c="white"/>}
        </button>
      </nav>
      <div className={`mobile-nav${navOpen ? " open" : ""}`}>
        <a href="#services" onClick={()=>setNavOpen(false)}>Services</a>
        <a href="#apps" onClick={()=>setNavOpen(false)}>Apps</a>
        <a href="#about" onClick={()=>setNavOpen(false)}>About</a>
        <a href="#contact" onClick={()=>setNavOpen(false)}>Contact</a>
      </div>

      {/* ── HERO ── */}
      <section className="hero">
        <div className="hero-grid"/>
        <div className="hero-glow"/>
        <div className="hero-glow2"/>
        <div className="hero-inner">
          <div>
            <div className="hero-badge">
              <div className="hero-badge-dot"/>
              <span className="hero-badge-text">South African Software Studio</span>
            </div>
            <h1>We build software<br/>that actually <span>works.</span></h1>
            <p className="hero-sub">Custom development, SaaS products, mobile apps and backend infrastructure — engineered properly, delivered on time.</p>
            <div className="hero-btns">
              <a href="#contact" className="btn-primary">
                Start a Project <I.Arrow s={16} c="white"/>
              </a>
              <a href="#apps" className="btn-outline">
                Explore Our Apps
              </a>
            </div>
          </div>
          <div className="hero-float">
            <div className="hero-float-title">What We Deliver</div>
            {[
              { Icon:I.Code,       bg:"rgba(45,124,246,0.18)",   title:"Custom Software",      sub:"Built around your exact needs" },
              { Icon:I.Layers,     bg:"rgba(99,102,241,0.18)",   title:"SaaS Platforms",       sub:"Scalable subscription products" },
              { Icon:I.Monitor,    bg:"rgba(8,145,178,0.18)",    title:"Websites & Apps",      sub:"Fast, modern, mobile-first" },
              { Icon:I.Cpu,        bg:"rgba(0,212,255,0.12)",    title:"AI Integration",       sub:"Intelligent features & automation" },
              { Icon:I.Server,     bg:"rgba(45,124,246,0.18)",   title:"Backend Services",     sub:"APIs, databases & cloud infra" },
            ].map(({ Icon, bg, title, sub }) => (
              <div key={title} className="hero-float-item">
                <div className="hero-float-icon" style={{background:bg}}>
                  <Icon s={18} c="var(--accent)"/>
                </div>
                <div>
                  <div className="hero-float-item-title">{title}</div>
                  <div className="hero-float-item-sub">{sub}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="hero-stats">
            {[["7+","Services Offered"],["100%","South African Built"],["24hr","Response Time"]].map(([n,l])=>(
              <div key={l}>
                <div className="hero-stat-num">{n}</div>
                <div className="hero-stat-label">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <div id="services"/>
      <div className="section services-bg">
        <div className="section-inner">
          <FadeIn>
            <div className="section-label">What We Do</div>
            <h2 className="section-title">Services built for real business problems</h2>
            <p className="section-sub">Every service is hands-on, no-shortcut development — written by engineers who take ownership of the outcome.</p>
          </FadeIn>
          <div className="svc-grid">
            {services.map(({ Icon, title, desc, accent, tags }, i) => (
              <FadeIn key={title} delay={i * 0.07}>
                <div className="svc-card">
                  <div className="svc-icon" style={{background:`${accent}1a`}}>
                    <Icon s={22} c={accent}/>
                  </div>
                  <div className="svc-title">{title}</div>
                  <div className="svc-desc">{desc}</div>
                  <div className="svc-tags">
                    {tags.map(t=><span key={t} className="svc-tag">{t}</span>)}
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>

      {/* ── APPS ── */}
      <div id="apps"/>
      <div className="apps-wrap">
        <div className="apps-inner">
          <FadeIn>
            <div className="apps-header">
              <div className="section-label">Our Apps</div>
              <h2 className="section-title">Products we ship and stand behind</h2>
              <p className="section-sub">We build our own apps alongside client work — each one is a live proof of what we can do.</p>
            </div>
          </FadeIn>

          {/* LunaCycle */}
          <FadeIn>
            <div className="app-row">
              <div>
                <div className="app-label" style={{color:"#a855f7"}}>iOS &amp; Android</div>
                <div className="app-name">LunaCycle</div>
                <div className="app-tagline" style={{color:"rgba(168,85,247,0.8)"}}>Cycle tracking, reimagined.</div>
                <div className="app-desc">A beautifully simple menstrual cycle and reproductive health tracker. Secure account sync, intelligent predictions, and symptom logging — all in one place. Your health data stays yours.</div>
                <div className="app-feats">
                  {["Cycle & ovulation tracking","Symptom &amp; mood logging","Period predictions","Secure Firebase sync","Private account login","No ads, no selling data"].map(f=>(
                    <div key={f} className="app-feat">
                      <div className="app-feat-dot" style={{background:"#a855f7"}}/>
                      <span dangerouslySetInnerHTML={{__html:f}}/>
                    </div>
                  ))}
                </div>
                <div className="app-actions">
                  <div className="store-btn">
                    <I.Smartphone s={16} c="white"/>
                    <div><div className="store-btn-sm">Download on the</div><div className="store-btn-name">App Store</div></div>
                  </div>
                  <div className="store-btn">
                    <I.Zap s={16} c="white"/>
                    <div><div className="store-btn-sm">Get it on</div><div className="store-btn-name">Google Play</div></div>
                  </div>
                  <button className="privacy-pill" onClick={()=>onNav("privacy-lunacycle")}>Privacy Policy</button>
                </div>
              </div>
              <div>
                <div className="mockup-stage">
                  <div className="mockup-phone-wrap"><LunaPhone/></div>
                  <div className="mockup-desktop-wrap"><LunaDesktop/></div>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* SplitEase */}
          <FadeIn delay={0.1}>
            <div className="app-row">
              <div>
                <div className="mockup-stage-rev">
                  <div className="mockup-phone-wrap"><SplitPhone/></div>
                  <div className="mockup-desktop-wrap"><SplitDesktop/></div>
                </div>
              </div>
              <div>
                <div className="app-label" style={{color:"#0d9488"}}>iOS &amp; Android</div>
                <div className="app-name">SplitEase</div>
                <div className="app-tagline" style={{color:"rgba(13,148,136,0.8)"}}>Split the bill. Keep your privacy.</div>
                <div className="app-desc">The fastest way to split any bill between friends, family, or colleagues. No account. No internet. No data collected — ever. Everything happens on your device and disappears when you're done.</div>
                <div className="app-feats">
                  {["Equal & custom splits","Multiple expense tracking","No account required","Completely offline","No data stored or sent","Free, forever"].map(f=>(
                    <div key={f} className="app-feat">
                      <div className="app-feat-dot" style={{background:"#0d9488"}}/>
                      {f}
                    </div>
                  ))}
                </div>
                <div className="app-actions">
                  <div className="store-btn">
                    <I.Smartphone s={16} c="white"/>
                    <div><div className="store-btn-sm">Download on the</div><div className="store-btn-name">App Store</div></div>
                  </div>
                  <div className="store-btn">
                    <I.Zap s={16} c="white"/>
                    <div><div className="store-btn-sm">Get it on</div><div className="store-btn-name">Google Play</div></div>
                  </div>
                  <button className="privacy-pill" onClick={()=>onNav("privacy-splitease")}>Privacy Policy</button>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Trots Oudtshoorn */}
          <FadeIn delay={0.2}>
            <div className="app-row">
              <div>
                <div className="app-label" style={{color:"#d97706"}}>Website</div>
                <div className="app-name">Trots Oudtshoorn</div>
                <div className="app-tagline" style={{color:"rgba(217,119,6,0.8)"}}>Local pride, all in one place.</div>
                <div className="app-desc">A hyper-local business directory and community app for Oudtshoorn — helping residents and visitors discover local businesses, events and services in one trusted place.</div>
                <div className="app-feats">
                  {["Local business directory","Community events","Easy to search & browse","Built for Oudtshoorn","Mobile-friendly","Actively maintained"].map(f=>(
                    <div key={f} className="app-feat">
                      <div className="app-feat-dot" style={{background:"#d97706"}}/>
                      <span>{f}</span>
                    </div>
                  ))}
                </div>
                <div className="app-actions">
                  <a className="store-btn" href="https://www.trotsoudtshoorn.co.za" target="_blank" rel="noopener noreferrer">
                    <I.Globe s={16} c="white"/>
                    <div><div className="store-btn-sm">Visit</div><div className="store-btn-name">trotsoudtshoorn.co.za</div></div>
                  </a>
                </div>
              </div>
              <div>
                <div className="mockup-stage">
                  <div className="mockup-desktop-wrap"><TrotDesktop/></div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>

      {/* ── WHY US ── */}
      <div id="about"/>
      <div className="section">
        <div className="section-inner">
          <FadeIn>
            <div className="section-label">Why Diswayne</div>
            <h2 className="section-title">We build it like it's ours</h2>
            <p className="section-sub">A small team with high standards. We treat every project as if our own business depends on it — because our reputation does.</p>
          </FadeIn>
          <div className="why-grid">
            {whyUs.map(({ Icon, title, desc, accent }, i) => (
              <FadeIn key={title} delay={i * 0.08}>
                <div className="why-card">
                  <div className="why-icon" style={{background:`${accent}18`}}>
                    <Icon s={22} c={accent}/>
                  </div>
                  <div>
                    <div className="why-title">{title}</div>
                    <div className="why-desc">{desc}</div>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>

      {/* ── PROCESS ── */}
      <div className="section process-bg">
        <div className="section-inner">
          <FadeIn>
            <div className="section-label" style={{color:"var(--accent)"}}>How We Work</div>
            <h2 className="section-title">From idea to live — without the confusion</h2>
            <p className="section-sub">A straightforward process that keeps you informed and in control at every stage.</p>
          </FadeIn>
          <div className="process-grid">
            {steps.map(({ n, title, desc }) => (
              <FadeIn key={n}>
                <div className="process-step">
                  <div className="process-num">{n}</div>
                  <div className="process-title">{title}</div>
                  <div className="process-desc">{desc}</div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </div>

      {/* ── CONTACT ── */}
      <div id="contact"/>
      <div className="section">
        <div className="section-inner">
          <FadeIn>
            <div className="section-label">Get In Touch</div>
            <h2 className="section-title">Let's build something together</h2>
            <p className="section-sub">Tell us what you're working on. We'll get back to you within 24 hours with honest feedback and a clear next step.</p>
          </FadeIn>
          <div className="contact-grid">
            <FadeIn delay={0.1}>
              <div>
                {[
                  [I.Mail,   "Email",         "support@getdiswayne.com"],
                  [I.Globe,  "Website",        "getdiswayne.com"],
                  [I.MapPin, "Based In",       "South Africa"],
                  [I.Clock,  "Response Time",  "Within 24 hours"],
                ].map(([Icon, label, value]) => (
                  <div key={label} className="contact-info-item">
                    <div className="contact-info-icon"><Icon s={18} c="var(--blue-light)"/></div>
                    <div>
                      <div className="contact-info-label">{label}</div>
                      <div className="contact-info-value">{value}</div>
                    </div>
                  </div>
                ))}
              </div>
            </FadeIn>
            <FadeIn delay={0.18}>
              {formSent ? (
                <div className="success-box">
                  <div className="success-icon"><I.Check s={24} c="#0d9488"/></div>
                  <div className="success-title">Message received!</div>
                  <div className="success-sub">We'll be in touch within 24 hours.</div>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="form-group">
                    <label className="form-label">Full Name</label>
                    <input className="form-input" type="text" required placeholder="Your name"
                      value={formData.name} onChange={e=>setFormData(f=>({...f,name:e.target.value}))}/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <input className="form-input" type="email" required placeholder="you@company.com"
                      value={formData.email} onChange={e=>setFormData(f=>({...f,email:e.target.value}))}/>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Service Interested In</label>
                    <select className="form-input"
                      value={formData.service} onChange={e=>setFormData(f=>({...f,service:e.target.value}))}>
                      <option value="">Select a service…</option>
                      <option>Custom Software Development</option>
                      <option>SaaS Product</option>
                      <option>Website Development</option>
                      <option>Mobile App (iOS / Android)</option>
                      <option>Maintenance &amp; Support</option>
                      <option>AI Integration</option>
                      <option>Backend &amp; Infrastructure</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Tell Us About Your Project</label>
                    <textarea className="form-input" required placeholder="A brief overview of what you need…"
                      value={formData.message} onChange={e=>setFormData(f=>({...f,message:e.target.value}))}/>
                  </div>
                  <button className="form-submit" type="submit" disabled={sending}>
                    {sending ? "Sending…" : "Send Message →"}
                  </button>
                </form>
              )}
            </FadeIn>
          </div>
        </div>
      </div>

      {/* ── FOOTER ── */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <div className="footer-brand-dot"/>
            <div className="footer-brand-name">Diswayne Tech Systems Pty Ltd</div>
          </div>
          <div className="footer-links">
            <a href="#services">Services</a>
            <a href="#apps">Apps</a>
            <a href="#about">About</a>
            <a href="#contact">Contact</a>
            <button className="footer-privacy-btn" onClick={()=>onNav("privacy")}>Privacy Centre</button>
          </div>
        </div>
        <div className="footer-copy">© 2026 Diswayne Tech Systems (Pty) Ltd. All rights reserved. Registered in South Africa.</div>
      </footer>
    </>
  );
}
